import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postData } from '../../../services/apiService';

const OtpVerify = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(60);
  const [shake, setShake] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  // Get user data from localStorage and generate bubbles
  useEffect(() => {
    const savedUser = localStorage.getItem('resetPasswordUser');
    if (savedUser) {
      setUserData(JSON.parse(savedUser));
    } else {
      // Redirect back if no user data
      navigate('/ForgetPassword');
    }

    const generateBubbles = () => {
      const newBubbles = Array.from({ length: 15 }, (_, index) => ({
        id: index,
        size: Math.random() * 60 + 20,
        left: Math.random() * 100,
        animationDuration: Math.random() * 20 + 10,
        animationDelay: Math.random() * 5,
        opacity: Math.random() * 0.3 + 0.1,
        blur: Math.random() * 10 + 5,
      }));
      setBubbles(newBubbles);
    };

    generateBubbles();

    // Timer countdown
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [navigate]);

  const handleOtpChange = (index, value) => {
    if (value.match(/^[0-9]?$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError('');

      // Move to next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    if (!userData) {
      setError('Session expired. Please start again.');
      setShake(true);
      setTimeout(() => {
        setShake(false);
        navigate('/ForgetPassword');
      }, 1500);
      return;
    }

    setLoading(true);
    setError('');
    setShake(false);

    try {
      // Prepare payload for OTP verification
      const payload = {
        OriginalId: "",
        Otp: otpString,
        ProjectId: "24" ,
        UserId: userData?.userId 
      };
      // Make API call to verify OTP
      const response = await postData("Authentication/forgot-password/verify-otp", payload);

      if (response.isError === false) {
        // OTP verified successfully
        setSuccess(true);
        
        // Update user data with verification token if available
        const updatedUserData = {
          ...userData,
          resetToken: response.result?.resetToken ,
          isOtpVerified: true
        };
        localStorage.setItem('resetPasswordUser', JSON.stringify(updatedUserData));
        
        // Redirect to reset password page after a delay
        setTimeout(() => {
          navigate('/ResetPassword');
        }, 1500);
      } else {
        // Handle API error
        setError(response.message || 'Invalid verification code');
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    } catch (err) {
      // Handle network or other errors
      console.error('API Error:', err);
      setError(err.message || 'An error occurred. Please try again.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (timer === 0 && userData) {
      setLoading(true);
      setError('');

      try {
        // Prepare payload for resend OTP
        const payload = {
          Email: userData.Email || userData.email,
          MobileNumber: ""
        };

        const response = await postData("Authentication/forgot-password/send-otp", payload);

        if (response.isError === false) {
          setTimer(60);
          setError('');
          // Show success message
          setTimeout(() => {
            setError('New verification code sent!');
          }, 100);
        } else {
          setError(response.message || 'Failed to resend code');
          setShake(true);
          setTimeout(() => setShake(false), 500);
        }
      } catch (err) {
        setError(err.message || 'Failed to resend code');
        setShake(true);
        setTimeout(() => setShake(false), 500);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 relative overflow-hidden">
      {/* Animated Bubbles */}
      <div className="absolute inset-0 overflow-hidden">
        {bubbles.map(bubble => (
          <div
            key={bubble.id}
            className="absolute rounded-full bg-gradient-to-br from-white/20 via-blue-200/15 to-purple-200/10 backdrop-blur-sm"
            style={{
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              left: `${bubble.left}%`,
              top: '110%',
              opacity: bubble.opacity,
              filter: `blur(${bubble.blur}px)`,
              animation: `floatUp ${bubble.animationDuration}s ease-in-out ${bubble.animationDelay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-400/15 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className={`bg-white/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 transition-all duration-300 ${shake ? 'animate-shake' : ''}`}>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-purple-50 shadow-lg border border-white/30">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Verify Code
              </h1>
              <p className="text-blue-700 mt-2 text-sm">
                Enter the 6-digit code sent to {userData?.Email || userData?.email || 'your email'}
              </p>
            </div>

            {/* OTP Inputs */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="flex justify-center space-x-3">
                  {otp.map((digit, index) => (
                    <div key={index} className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
                      <input
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="relative w-14 h-16 text-center text-3xl font-bold bg-white border-2 border-blue-200 rounded-xl text-blue-900 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/30 outline-none transition-all duration-300 z-10"
                        disabled={loading || success}
                        autoFocus={index === 0}
                      />
                    </div>
                  ))}
                </div>

                {/* Timer */}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center text-sm text-blue-700 bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {timer > 0 ? (
                      <span>Resend code in <span className="font-bold">{timer}s</span></span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={loading}
                        className="text-blue-600 hover:text-blue-700 font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Sending...' : 'Resend Code'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm animate-fadeIn">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm animate-fadeIn">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-semibold">Code verified successfully!</p>
                      <p className="text-sm text-green-600 mt-1">Redirecting...</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || success}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                <span className="relative flex items-center justify-center space-x-2">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : success ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Verified!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Verify Code</span>
                    </>
                  )}
                </span>
              </button>
            </form>

            {/* Back to Forgot Password */}
            <div className="mt-6 pt-6 border-t border-blue-100/30">
              <Link
                to="/ForgetPassword"
                className="inline-flex items-center justify-center w-full py-3 px-4 bg-white/50 hover:bg-white/70 text-blue-700 font-medium rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-300 group"
              >
                <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Forgot Password
              </Link>
            </div>

            {/* Copyright */}
            <div className="mt-6 pt-4 border-t border-blue-100/20 text-center">
              <p className="text-blue-600 text-xs">
                © {new Date().getFullYear()} Event Management System. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerify;